<template>
    <div class="main-panel">
        <h1 class="result-title">User Cap Report</h1>

        <div class="content">
            <div class="form-box">
                <div class="form-content">
                    <div class="inputbox">
                        <input type="month" id="reportDate" v-model="selectedMonth" />
                    </div>
                    <div class="button-container">
                        <button @click="loadReport" :disabled="isLoading || !selectedMonth">
                            <span v-if="!isLoading">Run Report</span>
                            <LoaderDots v-if="isLoading" />
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="hasSearched && !isLoading">
                <div class="table-content">
                    <div v-if="items.length === 0" class="no-results">
                        No users exceeded £400 in the selected month.
                    </div>

                    <table v-else>
                        <thead>
                            <tr class="table-header">
                                <th>User ID</th>
                                <th>Total Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="table-row" v-for="item in items" :key="item.userId">
                                <td>
                                    <a :href="`/users/${item.userId}`">{{ item.userId }}</a>
                                </td>
                                <td>£{{ item.totalAmount.toFixed(2) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import reportingService from "../services/reporting-service";
import { UserCapReportItem } from "../responses/UserCapReportResponse";
import LoaderDots from "../components/LoadingButtonDots.vue";

export default defineComponent({
    name: "UserCapReportView",
    components: {
        LoaderDots,
    },
    setup() {
        const selectedMonth = ref<string>("");
        const items = ref<UserCapReportItem[]>([]);
        const isLoading = ref(false);
        const hasSearched = ref(false);

        async function loadReport() {
            if (!selectedMonth.value) return;

            isLoading.value = true;
            hasSearched.value = false;
            items.value = [];

            const reportDate = `${selectedMonth.value}-01`;
            const response = await reportingService.getUserCapReport(reportDate);

            isLoading.value = false;
            hasSearched.value = true;

            if (!response || !response.success) return;

            items.value = response.items;
        }

        return {
            selectedMonth,
            items,
            isLoading,
            hasSearched,
            loadReport,
        };
    },
});
</script>

<style scoped>
    .main-panel {
        padding: 3rem;
    }

    .result-title {
        position: relative;
        min-height: 89px;
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 2rem;
        background-color: var(--clr-primary);
        padding: 1rem 1rem;
        margin-inline: 2rem;
        color: white;
        font-size: 24px;
        border-radius: 3px;
        box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.42), 0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2);
        align-items: center;
        z-index: 1;
    }

    .content {
        overflow-x: auto;
        position: relative;
        background-color: #fff;
        padding: 80px 40px 20px 40px;
        box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
        border-radius: 3px;
        margin-top: -44px;
    }

    .form-box {
        margin-bottom: 2rem;
    }

    .form-content {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1rem;
        align-items: end;
    }

    .inputbox {
        position: relative;
        border-bottom: 2px solid #9d9c9c;
    }

        .inputbox input {
            width: 100%;
            height: 65px;
            background: transparent;
            border: none;
            outline: none;
            font-size: 1em;
            padding: 0 10px;
            color: #333;
        }

    .button-container button {
        width: 100%;
        height: 65px;
        background-color: var(--clr-primary);
        color: white;
        border: none;
        border-radius: 3px;
        font-size: 1em;
        cursor: pointer;
    }

        .button-container button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

    table {
        width: 100%;
        border-collapse: collapse;
        background-color: white;
    }

    td {
        padding: 0.75rem 1rem;
        text-align: left;
        border-top: 1px solid #ddd;
    }

    th {
        padding: 0.75rem 1rem;
        text-align: left;
        font-weight: bold;
    }

    .table-row:hover {
        background-color: #f1f1f1;
    }

    .no-results {
        padding: 1rem 0;
        color: #666;
    }
</style>
